import { AgentKnowledgeStatus } from "@redotech/cockroachdb-codegen/tables/agent-knowledge/agent-knowledge-status";
import { MerchantKnowledgeOrigin } from "@redotech/cockroachdb-codegen/tables/agent-knowledge/merchant-knowledge-origin";
import { accountIdFormat } from "@redotech/redo-account-id";
import { productFamilyIdFormat } from "@redotech/redo-product-id";
import {
  agentKnowledgeIdFormat,
  merchantKnowledgeIdFormat,
} from "@redotech/shopper-agent-knowledge-id";
import * as z from "zod";

// The shapes a merchant surface works in — no SQL row types, no registry rows.
// Safe to import from the frontend, and parseable at an RPC boundary.

/**
 * `@`-mention references derived from an article's content. The full set is
 * rewritten on every save, so it must always mirror the current content.
 */
/** The internal id is canonical; `shopifyId` rides along so a merchant
 * surface can show the product without resolving it again. */
export const merchantKnowledgeProductReferenceSchema = z.object({
  productFamilyId: productFamilyIdFormat.schema,
  shopifyId: z.string().nullable(),
});
export type MerchantKnowledgeProductReference = z.infer<
  typeof merchantKnowledgeProductReferenceSchema
>;

export const merchantKnowledgeReferencesSchema = z.object({
  products: z.array(merchantKnowledgeProductReferenceSchema),
  merchantKnowledgeIds: z.array(merchantKnowledgeIdFormat.schema),
});
export type MerchantKnowledgeReferences = z.infer<
  typeof merchantKnowledgeReferencesSchema
>;

/**
 * What a merchant authors. Deliberately cannot express the retrieval key or
 * the registry state — those are not theirs to set.
 */
export const merchantKnowledgeDraftSchema = z.object({
  title: z.string(),
  content: z.string(),
  /** Shopify ids are accepted here; the handler resolves them to families. */
  shopifyProductIds: z.array(z.string()).optional(),
  referencedKnowledgeIds: z.array(merchantKnowledgeIdFormat.schema).optional(),
});
export type MerchantKnowledgeDraft = z.infer<
  typeof merchantKnowledgeDraftSchema
>;

/**
 * The text the agent matches on. Machine-owned: derived from the article's
 * title and content, never merchant-edited.
 */
export const retrievalKeySchema = z.object({
  value: z.string(),
  /**
   * Whether `value` describes the article's *current* text — derived by
   * comparing stored text to stored checksum, so it cannot lie.
   */
  fresh: z.boolean(),
});
export type RetrievalKey = z.infer<typeof retrievalKeySchema>;

/** When the agent may retrieve this. Lives on the registry entry. */
export const merchantKnowledgeAvailabilitySchema = z.object({
  status: z.enum(AgentKnowledgeStatus),
  /** Half-open window: start <= now < end. Null is open-ended. */
  effectiveStartAt: z.date().nullable(),
  effectiveEndAt: z.date().nullable(),
});
export type MerchantKnowledgeAvailability = z.infer<
  typeof merchantKnowledgeAvailabilitySchema
>;

export const merchantKnowledgeArticleSchema = z.object({
  id: merchantKnowledgeIdFormat.schema,
  /** Registry id. Retrieval hits carry this, not `id`. */
  agentKnowledgeId: agentKnowledgeIdFormat.schema,
  accountId: accountIdFormat.schema,
  origin: z.enum(MerchantKnowledgeOrigin),
  title: z.string(),
  content: z.string(),
  references: merchantKnowledgeReferencesSchema,
  retrievalKey: retrievalKeySchema,
  availability: merchantKnowledgeAvailabilitySchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type MerchantKnowledgeArticle = z.infer<
  typeof merchantKnowledgeArticleSchema
>;

/**
 * The article plus the counted joins a merchant surface shows. Named for the
 * repo read behind it — `getWithUsageById`, `listWithUsage`.
 */
export const merchantKnowledgeArticleWithUsageSchema =
  merchantKnowledgeArticleSchema.extend({
    /** Messages this article was used for, from `ai_knowledge_use`. */
    useCount: z.number(),
    recommendedShopifyProductIds: z.array(z.string()),
  });
export type MerchantKnowledgeArticleWithUsage = z.infer<
  typeof merchantKnowledgeArticleWithUsageSchema
>;

/** A row in a management list: the above minus its body, which no list renders. */
export const merchantKnowledgeListItemSchema =
  merchantKnowledgeArticleWithUsageSchema.omit({ content: true });
export type MerchantKnowledgeListItem = z.infer<
  typeof merchantKnowledgeListItemSchema
>;

export const DRAFT_AVAILABILITY: MerchantKnowledgeAvailability = {
  status: AgentKnowledgeStatus.Draft,
  effectiveStartAt: null,
  effectiveEndAt: null,
};

export const LIVE_AVAILABILITY: MerchantKnowledgeAvailability = {
  status: AgentKnowledgeStatus.Active,
  effectiveStartAt: null,
  effectiveEndAt: null,
};

export const DISABLED_AVAILABILITY: MerchantKnowledgeAvailability = {
  status: AgentKnowledgeStatus.Disabled,
  effectiveStartAt: null,
  effectiveEndAt: null,
};
